import { TelnyxRTC } from '@telnyx/webrtc'
import type { Call as TelnyxRtcCall, INotification } from '@telnyx/webrtc'

// The phone on Telnyx (CAL-78), for reps whose account runs calls there.
//
// TeXML has no `<Client>`, so a rep's app is a Telnyx SIP credential: the
// token endpoint hands this app a Telnyx login JWT instead of a Twilio access
// token, and Callingly rings the app by dialling that credential. useCall was
// built around Twilio's Device and Call, with years of recovery logic keyed to
// their states, events and error codes. Rather than fork it, TelnyxDevice and
// TelnyxCall present the parts of those two classes useCall, useAudioDevices
// and the pusher store use, over @telnyx/webrtc.
//
// Two things differ from Twilio and are handled here:
// - Placing a call. TeXML has nothing like Twilio's outgoing TwiML app, so the
//   backend turns an app's dial around: it rings the app with an
//   `X-Callingly-App-Dial` header, and the app answers its own call. connect()
//   waits for that invite and answers it.
// - Who is calling. Twilio puts the call_user id in the `From` of an incoming
//   call (`client:{id}`); Telnyx sends it as `X-Callingly-Call-User-Id`, which
//   TelnyxCall reports as the same `From`.

type Listener = (...args: never[]) => void

class Emitter {
  private listeners = new Map<string, Set<Listener>>()

  on(event: string, listener: Listener) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(listener)

    return this
  }

  off(event: string, listener: Listener) {
    this.listeners.get(event)?.delete(listener)

    return this
  }

  emit(event: string, ...args: unknown[]) {
    this.listeners
      .get(event)
      ?.forEach((listener) => (listener as (...a: unknown[]) => void)(...args))
  }

  // Twilio's classes are Node EventEmitters; useCall raises their limit.
  setMaxListeners() {
    return this
  }
}

// Shaped like a Twilio TwilioError, so useCall's device and call error
// handlers branch on it unchanged. `telnyxCode` keeps the SDK's own code for
// Sentry.
export class TelnyxDeviceError extends Error {
  constructor(
    public code: number,
    message: string,
    public telnyxCode?: number,
  ) {
    super(message)
    this.name = 'TelnyxDeviceError'
  }
}

// Telnyx error codes useCall must treat as the Twilio errors it already
// recovers from. Anything else keeps its Telnyx code and falls through to the
// generic report-and-reset branch.
const TWILIO_EQUIVALENT_CODES: Record<number, number> = {
  // Socket trouble the SDK reconnects from on its own (Twilio 31005), so the
  // signaling watchdog gets the same chance to rebuild a wedged phone.
  45001: 31005, // WEBSOCKET_CONNECTION_FAILED
  45002: 31005, // WEBSOCKET_ERROR
  45003: 31005, // RECONNECTION_EXHAUSTED
  45004: 31005, // GATEWAY_FAILED
  48001: 31009, // NETWORK_OFFLINE
  // A login the server refused: fetch a fresh token and start over (Twilio 20101).
  46001: 20101, // LOGIN_FAILED
  46002: 20101, // INVALID_CREDENTIALS
  46003: 20101, // AUTHENTICATION_REQUIRED
  // The microphone (Twilio 31401 permission, 31402 acquisition).
  42001: 31401, // MEDIA_MICROPHONE_PERMISSION_DENIED
  42002: 31402, // MEDIA_DEVICE_NOT_FOUND
  42003: 31402, // MEDIA_GET_USER_MEDIA_FAILED
}

type TelnyxErrorLike = { code?: number; message?: string; name?: string }

const toDeviceError = (event: unknown): TelnyxDeviceError => {
  const error = ((event as { error?: TelnyxErrorLike })?.error ?? event ?? {}) as TelnyxErrorLike
  const telnyxCode = typeof error.code === 'number' ? error.code : undefined
  const code = (telnyxCode && TWILIO_EQUIVALENT_CODES[telnyxCode]) ?? telnyxCode ?? 0

  return new TelnyxDeviceError(code, error.message ?? error.name ?? 'Telnyx error', telnyxCode)
}

// Telnyx call-quality warnings, by the names Twilio's Call 'warning' event
// uses. Telnyx doesn't say when one clears, so each is cleared once it stops
// recurring.
const WARNING_NAMES: Record<number, string> = {
  31001: 'high-rtt',
  31002: 'high-jitter',
  31003: 'high-packet-loss',
  31004: 'low-mos',
  32001: 'low-bytes-received',
  32002: 'low-bytes-sent',
  33001: 'ice-connectivity-lost',
}
const WARNING_CLEARS_AFTER_MS = 10000

const TOKEN_EXPIRING_SOON = 34001

// How long a placed call waits for Callingly to ring it back, as the backend
// gives that ring (Call::APP_CONNECT_TIMEOUT_SECONDS).
export const APP_DIAL_TIMEOUT_MS = 30000

const HEADER_PREFIX = 'x-callingly-'

const customHeaders = (call: TelnyxRtcCall): Record<string, string> => {
  const headers: Record<string, string> = {}
  const list = (call.options?.customHeaders ?? []) as Array<{ name: string; value: string }>

  list.forEach(({ name, value }) => {
    if (name?.toLowerCase().startsWith(HEADER_PREFIX)) {
      headers[name.toLowerCase().slice(HEADER_PREFIX.length)] = value
    }
  })

  return headers
}

export type TelnyxCallStatus = 'pending' | 'connecting' | 'open' | 'reconnecting' | 'closed'

// The Telnyx call states, as Twilio's Call.status() reports a call.
const statusFor = (state: string, direction: 'INCOMING' | 'OUTGOING'): TelnyxCallStatus => {
  switch (state) {
    case 'active':
    case 'held':
      return 'open'
    case 'recovering':
      return 'reconnecting'
    case 'hangup':
    case 'destroy':
    case 'purge':
      return 'closed'
    case 'answering':
      return 'connecting'
    default:
      return direction === 'INCOMING' ? 'pending' : 'connecting'
  }
}

type AudioChoices = { micId?: string; speakerId?: string }

export class TelnyxCall extends Emitter {
  readonly parameters: Record<string, string>
  readonly headers: Record<string, string>
  private answered = false
  private ended = false
  private locallyRejected = false
  private wasRecovering = false
  private warningTimers = new Map<string, number>()
  private volumeTimer: number | null = null
  private audioContext: AudioContext | null = null

  constructor(
    public raw: TelnyxRtcCall,
    public direction: 'INCOMING' | 'OUTGOING',
    private audioChoices: () => AudioChoices,
  ) {
    super()
    this.headers = customHeaders(raw)
    this.parameters = {
      From: `client:${this.headers['call-user-id'] ?? ''}`,
      CallSid: raw.id,
    }
  }

  get callUserId(): number | null {
    const id = Number(this.headers['call-user-id'])

    return Number.isFinite(id) && id > 0 ? id : null
  }

  get isAppDial(): boolean {
    return this.headers['app-dial'] === '1'
  }

  // No Callingly headers at all: TeXML's CustomHeaders never made it to the
  // SIP invite (unverified live as of CAL-78).
  get isUnlabelled(): boolean {
    return Object.keys(this.headers).length === 0
  }

  status(): TelnyxCallStatus {
    const status = statusFor(this.raw.state, this.direction)

    // A recovered call is set up again from scratch, through states that would
    // otherwise read as a new ring.
    return this.wasRecovering && status !== 'open' && status !== 'closed' ? 'reconnecting' : status
  }

  // Telnyx recovers a call after a network drop by hanging up its call object
  // and answering a new one for the same call; from then on that one is this
  // call.
  adopt(raw: TelnyxRtcCall) {
    this.stopVolume()
    this.raw = raw
  }

  accept() {
    this.raw.stopRingtone()
    const { micId, speakerId } = this.audioChoices()

    return this.raw.answer({
      ...(micId && micId !== 'default' ? { micId } : {}),
      ...(speakerId && speakerId !== 'default' ? { speakerId } : {}),
    })
  }

  reject() {
    this.locallyRejected = true
    this.raw.stopRingtone()
    void this.raw.hangup()
  }

  disconnect() {
    void this.raw.hangup()
  }

  mute(shouldMute = true) {
    if (shouldMute) {
      this.raw.muteAudio()
    } else {
      this.raw.unmuteAudio()
    }
  }

  isMuted(): boolean {
    return this.raw.isAudioMuted
  }

  sendDigits(digits: string) {
    this.raw.dtmf(digits)
  }

  setInputDevice(deviceId: string) {
    if (this.status() === 'open') {
      return this.raw.setAudioInDevice(deviceId)
    }
  }

  // A state change from the client's callUpdate notification, turned into the
  // events Twilio's Call emits.
  update() {
    const state = this.raw.state

    if (state === 'active') {
      if (!this.answered) {
        this.answered = true
        this.emit('accept', this)
      }
      this.startVolume()
    }

    if (state === 'recovering' && !this.wasRecovering) {
      this.wasRecovering = true
      this.emit('reconnecting', new TelnyxDeviceError(53405, 'Telnyx call recovering'))
    } else if (this.wasRecovering && (state === 'active' || state === 'held')) {
      this.wasRecovering = false
      this.emit('reconnected')
    }

    if (this.status() === 'closed' && !this.ended) {
      this.ended = true
      this.stopVolume()
      this.warningTimers.forEach((timer) => clearTimeout(timer))
      this.warningTimers.clear()

      // As on Twilio: a ring that ends unanswered is a cancel, an answered
      // call a disconnect, and a call the rep rejected neither.
      if (this.locallyRejected) {
        return
      }
      this.emit(this.answered ? 'disconnect' : 'cancel', this)
    }
  }

  warn(code: number) {
    const name = WARNING_NAMES[code]
    if (!name || this.ended) return

    const pending = this.warningTimers.get(name)
    if (pending) {
      clearTimeout(pending)
    } else {
      this.emit('warning', name)
    }

    this.warningTimers.set(
      name,
      window.setTimeout(() => {
        this.warningTimers.delete(name)
        this.emit('warning-cleared', name)
      }, WARNING_CLEARS_AFTER_MS),
    )
  }

  fail(error: TelnyxDeviceError) {
    this.emit('error', error)
  }

  // Twilio's Call emits input and output levels for the volume meters; Telnyx
  // doesn't, so they are read off the call's streams.
  private startVolume() {
    const Context = window.AudioContext
    if (!Context || this.volumeTimer) return

    try {
      const context = new Context()
      const meter = (stream?: MediaStream) => {
        if (!stream?.getAudioTracks().length) return null
        const analyser = context.createAnalyser()
        analyser.fftSize = 256
        context.createMediaStreamSource(stream).connect(analyser)
        return analyser
      }
      const level = (analyser: AnalyserNode | null) => {
        if (!analyser) return 0
        const samples = new Uint8Array(analyser.fftSize)
        analyser.getByteTimeDomainData(samples)
        return samples.reduce((peak, sample) => Math.max(peak, Math.abs(sample - 128) / 128), 0)
      }
      const input = meter(this.raw.localStream)
      const output = meter(this.raw.remoteStream)

      this.audioContext = context
      this.volumeTimer = window.setInterval(
        () => this.emit('volume', level(input), level(output)),
        100,
      )
    } catch {
      // Meters are cosmetic; a call without them is fine.
    }
  }

  private stopVolume() {
    if (this.volumeTimer) {
      clearInterval(this.volumeTimer)
      this.volumeTimer = null
    }
    void this.audioContext?.close().catch(() => {})
    this.audioContext = null
  }
}

// What useAudioDevices reads and sets on a Twilio Device's `audio`: the device
// lists, the microphone and the speaker. Telnyx takes the microphone and
// speaker per call, so the choices are kept here and applied as calls start.
class TelnyxAudio extends Emitter {
  availableInputDevices = new Map<string, MediaDeviceInfo>()
  availableOutputDevices = new Map<string, MediaDeviceInfo>()
  micId?: string
  speakerId?: string
  private readonly onDeviceChange = () => void this.refresh()

  constructor(private device: TelnyxDevice) {
    super()
    navigator.mediaDevices?.addEventListener?.('devicechange', this.onDeviceChange)
    void this.refresh()
  }

  async refresh() {
    try {
      const devices = (await navigator.mediaDevices?.enumerateDevices?.()) ?? []
      this.availableInputDevices = new Map(
        devices.filter((d) => d.kind === 'audioinput').map((d) => [d.deviceId, d]),
      )
      this.availableOutputDevices = new Map(
        devices.filter((d) => d.kind === 'audiooutput').map((d) => [d.deviceId, d]),
      )
      this.emit('deviceChange')
    } catch {
      // Enumeration can fail without permission; the lists stay as they were.
    }
  }

  async setInputDevice(deviceId: string) {
    this.micId = deviceId
    await this.device.activeCall()?.setInputDevice(deviceId)
  }

  async unsetInputDevice() {}

  speakerDevices = {
    get: () =>
      new Set(
        [this.availableOutputDevices.get(this.speakerId ?? 'default')].filter(
          (d): d is MediaDeviceInfo => !!d,
        ),
      ),
    set: async (deviceId: string) => {
      this.speakerId = deviceId
      this.device.setSpeaker(deviceId)
    },
  }

  // Telnyx plays its ringtone through its own audio element, which it doesn't
  // expose, so the ring follows the system default.
  ringtoneDevices = {
    set: async () => {},
  }

  disconnect() {}

  destroy() {
    navigator.mediaDevices?.removeEventListener?.('devicechange', this.onDeviceChange)
  }
}

export type TelnyxDeviceState = 'unregistered' | 'registering' | 'registered' | 'destroyed'

type PendingDial = {
  callUserId: number
  resolve: (call: TelnyxCall) => void
  reject: (error: Error) => void
  timer: number
}

export class TelnyxDevice extends Emitter {
  state: TelnyxDeviceState = 'unregistered'
  readonly audio: TelnyxAudio
  private client: TelnyxRTC
  private calls = new Map<string, TelnyxCall>()
  // App-dial invites that arrived before connect() asked for them: the
  // backend rings the app inside the request that connect() follows.
  private unclaimedDials: TelnyxCall[] = []
  private pendingDial: PendingDial | null = null
  // Until when a dial is in flight (expectDial), so a ring without headers
  // can still be taken as its ring-back.
  private dialExpectedUntil = 0
  // Whether useCall was told this phone is registered and not since told
  // otherwise: what its `registered` flag holds.
  private announcedRegistered = false
  private remoteAudio: HTMLAudioElement

  constructor(token: string, options: { ringtoneFile?: string; debug?: boolean } = {}) {
    super()

    this.remoteAudio = document.createElement('audio')
    this.remoteAudio.autoplay = true
    this.remoteAudio.hidden = true
    document.body.appendChild(this.remoteAudio)

    this.client = new TelnyxRTC({
      login_token: token,
      ringtoneFile: options.ringtoneFile,
      debug: options.debug,
    })
    this.client.remoteElement = this.remoteAudio
    this.audio = new TelnyxAudio(this)

    this.client.on('telnyx.ready', () => this.setState('registered'))
    this.client.on('telnyx.socket.close', () => {
      // The SDK reconnects and logs in again by itself; until then the phone
      // can't ring, as Twilio's does while its signaling is down.
      if (this.state === 'registered') {
        this.setState('registering')
      }
    })
    this.client.on('telnyx.error', (event: unknown) => this.onError(event))
    this.client.on('telnyx.warning', (event: unknown) => this.onWarning(event))
    this.client.on('telnyx.notification', (notification: INotification) =>
      this.onNotification(notification),
    )
  }

  get isBusy(): boolean {
    return [...this.calls.values()].some((call) => call.status() !== 'closed')
  }

  activeCall(): TelnyxCall | undefined {
    return [...this.calls.values()].find((call) => call.status() === 'open')
  }

  setSpeaker(deviceId: string) {
    this.client.speaker = deviceId
    const sink = this.remoteAudio as HTMLAudioElement & {
      setSinkId?: (id: string) => Promise<void>
    }
    void sink.setSinkId?.(deviceId).catch(() => {})
  }

  register(): Promise<void> {
    if (this.state === 'registered') {
      return Promise.resolve()
    }

    this.setState('registering')

    return new Promise((resolve, reject) => {
      const settle = (registered: boolean, error?: unknown) => {
        this.off('registered', onRegistered)
        this.off('error', onError)
        if (registered) resolve()
        else reject(error)
      }
      const onRegistered = () => settle(true)
      const onError = (error: TelnyxDeviceError) => {
        if (error.code === 20101) settle(false, error)
      }

      this.on('registered', onRegistered)
      this.on('error', onError as Listener)
      this.client.connect().catch((error) => settle(false, error))
    })
  }

  // A fresh JWT logs the live connection in again, and is what the SDK logs
  // in with after a reconnect.
  updateToken(token: string) {
    void this.client.login({ creds: { login_token: token } }).catch(() => {})
  }

  // The rep is placing a call: Callingly is about to ring this app back for
  // it, maybe before connect() is called.
  expectDial() {
    this.dialExpectedUntil = Date.now() + APP_DIAL_TIMEOUT_MS
  }

  // The dial failed: no ring-back is coming, so the next ring without headers
  // is an ordinary incoming call, and one already held for it is hung up.
  cancelDial() {
    this.dialExpectedUntil = 0
    this.unclaimedDials.forEach((call) => call.reject())
    this.unclaimedDials = []
  }

  // Twilio-style: the placed call, once Callingly has rung this app for it and
  // the app has answered.
  connect({ params }: { params: Record<string, string | number> }): Promise<TelnyxCall> {
    const callUserId = Number(params.call_user_id)

    const waiting = this.unclaimedDials.find(
      (call) => call.callUserId === callUserId || call.callUserId === null,
    )
    if (waiting) {
      this.unclaimedDials = this.unclaimedDials.filter((call) => call !== waiting)
      return this.answerDial(waiting)
    }

    return new Promise((resolve, reject) => {
      this.pendingDial = {
        callUserId,
        resolve,
        reject,
        timer: window.setTimeout(() => {
          this.pendingDial = null
          reject(new Error('Telnyx never rang the app for its call'))
        }, APP_DIAL_TIMEOUT_MS),
      }
    })
  }

  destroy() {
    const wasUp =
      this.announcedRegistered || this.state === 'registered' || this.state === 'registering'

    if (this.pendingDial) {
      clearTimeout(this.pendingDial.timer)
      this.pendingDial.reject(new Error('Phone was shut down'))
      this.pendingDial = null
    }
    this.calls.forEach((call) => call.disconnect())
    this.audio.destroy()
    void this.client.disconnect().catch(() => {})
    this.remoteAudio.remove()

    // Twilio's destroy() reports the device unregistered on its way down, and
    // useCall restarts the phone from that.
    if (wasUp) {
      this.state = 'unregistered'
      this.announcedRegistered = false
      this.emit('unregistered')
    }
    this.state = 'destroyed'
  }

  private setState(state: TelnyxDeviceState) {
    if (this.state === state || this.state === 'destroyed') return

    const was = this.state
    this.state = state

    // useCall resets the screen on 'registered'. A socket that dropped and
    // came back under a live call must not end that call's screen.
    if (state === 'registered' && !this.isBusy) {
      this.announcedRegistered = true
      this.emit('registered')
    } else if (state === 'unregistered' && was === 'registered') {
      this.announcedRegistered = false
      this.emit('unregistered')
    }
  }

  private async answerDial(call: TelnyxCall): Promise<TelnyxCall> {
    call.direction = 'OUTGOING'
    await call.accept()

    // answer() resolves even when the call couldn't be set up (no
    // microphone, say): the SDK hangs it up and reports the error itself.
    if (call.status() === 'closed') {
      throw new Error('Telnyx call ended while being answered')
    }

    return call
  }

  private onNotification(notification: INotification) {
    if (notification.type !== 'callUpdate' || !notification.call) return

    const raw = notification.call
    const recoveredId = (raw as { recoveredCallId?: string }).recoveredCallId
    let call = this.calls.get(raw.id) ?? (recoveredId ? this.calls.get(recoveredId) : undefined)

    if (call && call.raw !== raw) {
      // The replacement for a recovered call takes over; anything else from
      // the call object it replaced is stale.
      if (!recoveredId) return
      call.adopt(raw)
      this.calls.set(raw.id, call)
    }

    if (!call) {
      if (raw.direction !== 'inbound' || raw.state !== 'ringing') return

      call = new TelnyxCall(raw, 'INCOMING', () => ({
        micId: this.audio.micId,
        speakerId: this.audio.speakerId,
      }))
      this.calls.set(raw.id, call)

      // Without headers, the first ring while a dial is in flight is taken as
      // its ring-back: a rep placing a call isn't offered other calls.
      if (call.isAppDial || (call.isUnlabelled && Date.now() < this.dialExpectedUntil)) {
        this.dialExpectedUntil = 0
        this.claimDial(call)
      } else {
        this.emit('incoming', call)
      }
    }

    call.update()

    if (call.status() === 'closed') {
      this.calls.forEach((tracked, id) => tracked === call && this.calls.delete(id))
      this.unclaimedDials = this.unclaimedDials.filter((dial) => dial !== call)
    }
  }

  // An app-dial invite is never offered to the rep as an incoming call: it is
  // the call they just placed, so it is answered for connect(), or held until
  // connect() asks. One nobody asks for is hung up.
  private claimDial(call: TelnyxCall) {
    call.raw.stopRingtone()
    const pending = this.pendingDial

    // An invite without the call_user header (should TeXML ever drop it) can
    // still only be the dial in flight.
    if (pending && (call.callUserId === null || call.callUserId === pending.callUserId)) {
      clearTimeout(pending.timer)
      this.pendingDial = null
      this.answerDial(call).then(pending.resolve, pending.reject)
      return
    }

    this.unclaimedDials.push(call)
    window.setTimeout(() => {
      if (this.unclaimedDials.includes(call)) {
        this.unclaimedDials = this.unclaimedDials.filter((dial) => dial !== call)
        call.reject()
      }
    }, APP_DIAL_TIMEOUT_MS)
  }

  private onError(event: unknown) {
    const error = toDeviceError(event)
    const callId = (event as { callId?: string })?.callId
    const call = callId ? this.calls.get(callId) : undefined

    // A media failure on one call ends that call, as Twilio's call-scoped
    // errors do; everything else is the device's.
    if (call && error.code !== 31005 && error.code !== 31009) {
      call.fail(error)
      return
    }

    // A refused login. useCall destroys a phone it was told is registered,
    // which restarts it from 'unregistered'; one that never got there it
    // simply starts again, so this one is shut down here.
    if (error.code === 20101 && !this.announcedRegistered && this.state !== 'destroyed') {
      this.state = 'unregistered'
      void this.client.disconnect().catch(() => {})
    }

    this.emit('error', error)
  }

  private onWarning(event: unknown) {
    const { warning, callId } = (event ?? {}) as { warning?: { code?: number }; callId?: string }
    const code = warning?.code
    if (typeof code !== 'number') return

    if (code === TOKEN_EXPIRING_SOON) {
      this.emit('tokenWillExpire', this)
      return
    }

    const call = callId ? this.calls.get(callId) : this.activeCall()
    call?.warn(code)
  }
}
